import { jsx, Fragment, jsxs } from "react/jsx-runtime";
import { memo, useState, useRef, useEffect, useCallback } from "react";
import { useGoogleMap } from "@react-google-maps/api";
import { preloadPinImages } from "../../Map.utils.js";
import Clusterer from "./Clusterer.js";
import Marker from "./Marker.js";
const GeoObjects = ({
  pins,
  selected,
  hovered,
  // cluster
  enableClustering,
  clusterImage,
  clusterHoverImage,
  clusterSize,
  clusterHoverSize,
  // placemark
  placemarkImage,
  placemarkHoverImage,
  placemarkActiveImage,
  placemarkSize,
  placemarkHoverSize,
  placemarkActiveSize,
  placemarkOffset,
  placemarkHoverOffset,
  placemarkActiveOffset,
  // zoom
  shouldZoomOnClickPin,
  // handlers
  onSelectPin,
  onHoverPin
}) => {
  const map = useGoogleMap();
  const [hiddenPinIds, setHiddenPinIds] = useState([]);
  const [isClusteringReady, setIsClusteringReady] = useState(false);
  const isMounted = useRef({ selected: false, hovered: false });
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [hoveredMarker, setHoveredMarker] = useState(null);
  useEffect(() => {
    if (!isMounted.current.selected) {
      isMounted.current.selected = true;
      return;
    }
    setSelectedMarker(selected);
  }, [pins, selected]);
  useEffect(() => {
    if (!isMounted.current.hovered) {
      isMounted.current.hovered = true;
      return;
    }
    setHoveredMarker(hovered);
  }, [pins, hovered]);
  const handleUpdateClusterPins = useCallback((ids, ready) => {
    setHiddenPinIds(ids);
    if (ready) {
      setIsClusteringReady(true);
    }
  }, []);
  const base64CacheRef = useRef({});
  useEffect(() => {
    if (!map || !pins.length) {
      return;
    }
    preloadPinImages(pins, { default: placemarkImage, hover: placemarkHoverImage, active: placemarkActiveImage }).then(
      (cache) => {
        base64CacheRef.current = cache;
      }
    );
  }, [map, pins, placemarkImage, placemarkHoverImage, placemarkActiveImage]);
  const getMarkerImages = useCallback(
    (pin) => {
      var _a, _b, _c;
      const customImageDefault = getBase64((_a = pin == null ? void 0 : pin.placemark) == null ? void 0 : _a.default) || placemarkImage;
      const customImageHover = getBase64((_b = pin == null ? void 0 : pin.placemark) == null ? void 0 : _b.hover) || placemarkHoverImage;
      const customImageActive = getBase64((_c = pin == null ? void 0 : pin.placemark) == null ? void 0 : _c.active) || placemarkActiveImage;
      return {
        default: customImageDefault,
        hover: customImageHover,
        active: customImageActive
      };
    },
    [placemarkActiveImage, placemarkHoverImage, placemarkImage]
  );
  const getBase64 = (url) => url ? base64CacheRef.current[url] || url : void 0;
  const getVisiblePins = () => {
    if (!enableClustering) {
      return pins;
    }
    if (isClusteringReady) {
      return pins.filter((pin) => !hiddenPinIds.includes(pin.id));
    }
    return [];
  };
  const renderMarkers = useCallback(() => {
    const visiblePins = getVisiblePins();
    return /* @__PURE__ */ jsx(Fragment, { children: visiblePins.map((pin) => {
      return /* @__PURE__ */ jsx(
        Marker,
        {
          pin,
          isHovered: pin.id === hoveredMarker,
          isSelected: pin.id === selectedMarker,
          shouldZoomOnClickPin,
          placemarkSize,
          placemarkHoverSize,
          placemarkActiveSize,
          placemarkOffset,
          placemarkHoverOffset,
          placemarkActiveOffset,
          getImages: getMarkerImages,
          onHover: onHoverPin,
          onSelect: onSelectPin
        },
        pin.id
      );
    }) });
  }, [
    enableClustering,
    isClusteringReady,
    pins,
    hiddenPinIds,
    shouldZoomOnClickPin,
    placemarkSize,
    placemarkHoverSize,
    placemarkActiveSize,
    placemarkOffset,
    placemarkHoverOffset,
    placemarkActiveOffset,
    getMarkerImages,
    onHoverPin,
    onSelectPin,
    hoveredMarker,
    selectedMarker
  ]);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    enableClustering && clusterImage && /* @__PURE__ */ jsx(
      Clusterer,
      {
        pins,
        clusterImage,
        clusterHoverImage,
        clusterSize,
        clusterHoverSize,
        onUpdateClusterPins: handleUpdateClusterPins
      }
    ),
    renderMarkers()
  ] });
};
const GeoObjects$1 = memo(GeoObjects);
export {
  GeoObjects$1 as default
};
//# sourceMappingURL=GeoObjects.js.map
